import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  Box,
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
} from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'

import { TextInput, FileUploaderInput, ConfirmationDialog, useModalContext } from '../../../../ui'
import { requests } from '../../../../core/ajax'
import { feedback } from '../../../../core/feedback'
import { ValidateChangeDialog } from '../dialogs'
import { usePORequests } from '../../hooks'

export const InstaladorPrescriptorPanel = ({ instaladores }) => {
  const { formatMessage } = useIntl()
  const { poRequestId } = useParams()
  const { validateChangedFields } = usePORequests()
  const {
    actions: { open },
  } = useModalContext()
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [validatedLocal, setValidatedLocal] = useState({})

  if (!instaladores || instaladores.length === 0) return null

  const instalador = instaladores[selectedIdx]

  const handleValidateChange = (name, title) => {
    open({
      Component: ValidateChangeDialog,
      data: {
        cambios_pendientes: instalador.cambios_pendientes,
        requestId: poRequestId,
        name,
        title,
        validateChangedFields,
      },
      maxWidth: 'sm',
    })
  }

  const handleValidateInstaller = () => {
    open({
      Component: ConfirmationDialog,
      data: {
        title: formatMessage({
          id: 'pages.poRequests.detail.instaladoresPrescriptores.validate.title',
          defaultMessage: 'Confirmar validación',
        }),
        text: formatMessage({
          id: 'pages.poRequests.detail.instaladoresPrescriptores.validate.text',
          defaultMessage: '¿Estás seguro/a de que deseas validar este instalador prescriptor?',
        }),
        yesText: formatMessage({ id: 'global.yes', defaultMessage: 'Sí' }),
        noText: formatMessage({ id: 'global.no', defaultMessage: 'No' }),
        yesAction: async () => {
          try {
            await requests.patch(
              `/campana/adhesiones/${poRequestId}/instaladores_prescriptores/${instalador.id}/validar`
            )
            setValidatedLocal((prev) => ({ ...prev, [instalador.id]: true }))
            feedback(
              'success',
              formatMessage({
                id: 'pages.poRequests.detail.instaladoresPrescriptores.validate.success',
                defaultMessage: 'Instalador prescriptor validado correctamente',
              })
            )
          } catch (e) {
            feedback(
              'error',
              formatMessage({
                id: 'pages.poRequests.detail.instaladoresPrescriptores.validate.error',
                defaultMessage: 'Error al validar el instalador prescriptor',
              })
            )
          }
        },
      },
    })
  }

  const txt = (key) =>
    formatMessage({
      id: 'pages.poRequests.detail.instaladoresPrescriptores.fields.' + key + '.title',
    })

  const fields = [
    { name: 'razon_social', value: instalador.razon_social },
    { name: 'nif', value: instalador.nif },
    { name: 'email', value: instalador.email },
    { name: 'telefono', value: instalador.telefono },
  ]

  return (
    <Box
      sx={{
        marginBottom: '20px',
        padding: '18px',
        borderTopRightRadius: '20px',
        borderBottomLeftRadius: '20px',
        backgroundColor: 'grey.100',
        boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.25)',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6" color="primary" gutterBottom>
          {formatMessage({ id: 'pages.poRequests.detail.instaladoresPrescriptores.panel.title' })}
        </Typography>
        {instaladores.length > 1 && (
          <FormControl size="small" variant="outlined">
            <Select value={selectedIdx} onChange={(e) => setSelectedIdx(e.target.value)}>
              {instaladores.map((inst, i) => (
                <MenuItem key={inst.id} value={i}>
                  {inst.razon_social}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      <Grid container spacing={4} style={{ marginTop: 0, marginBottom: 0 }}>
        {fields.map((field) => (
          <Grid key={field.name} size={{ xs: 12, sm: 6, md: 3 }}>
            <Box display="flex" justifyContent="start">
              <Box display="block">
                <TextInput
                  readOnly
                  required={false}
                  title={txt(field.name)}
                  value={field.value}
                  name={field.name}
                  onChange={() => {}}
                />
              </Box>
              {instalador.cambios_pendientes?.some((c) => c.campo === field.name) && (
                <Box ml="0.5em">
                  <IconButton
                    size="small"
                    onClick={() => handleValidateChange(field.name, txt(field.name))}
                  >
                    <NotificationsActiveIcon color="secondary" style={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      {instalador.registro_industrial && (
        <Box mt={3} pt={2} borderTop="1px solid rgba(0, 0, 0, 0.12)">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box flex={1}>
              <FileUploaderInput
                readOnly
                currentDoc={instalador.registro_industrial}
                fileProps={{
                  maxSize: 10000000,
                  accept: ['application/pdf'],
                }}
                title={
                  <Box display="flex" alignItems="center">
                    {formatMessage({
                      id: 'pages.poRequests.detail.instaladoresPrescriptores.fields.registro_industrial.title',
                    })}
                    {instalador.cambios_pendientes?.some(
                      (c) => c.campo === 'registro_industrial'
                    ) && (
                      <Box ml="0.5em" display="flex">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleValidateChange(
                              'registro_industrial',
                              formatMessage({
                                id: 'pages.poRequests.detail.instaladoresPrescriptores.fields.registro_industrial.title',
                              })
                            )
                          }
                        >
                          <NotificationsActiveIcon color="secondary" style={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                }
              />
            </Box>
            {!instalador.validado && !validatedLocal[instalador.id] && (
              <Box>
                <Button variant="contained" color="primary" onClick={handleValidateInstaller}>
                  {formatMessage({
                    id: 'pages.poRequests.detail.instaladoresPrescriptores.validate.button',
                    defaultMessage: 'Validar instalador',
                  })}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
